import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

export type Language = 'SPA' | 'ENG' | 'POR' | 'ITA' | 'FRA';

interface SettingsContextType {
    language: Language;
    setLanguage: (language: Language) => void;
    useAinovisTemplates: boolean;
    setUseAinovisTemplates: (use: boolean) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const USE_AINOVIS_TEMPLATES_KEY = 'useAinovisTemplates';

export function SettingsProvider({ children }: { children: ReactNode }) {
    const [language, setLanguage] = useState<Language>('SPA');
    const [useAinovisTemplates, setUseAinovisTemplatesState] = useState<boolean>(() => {
        const stored = localStorage.getItem(USE_AINOVIS_TEMPLATES_KEY);
        return stored === null ? true : stored === 'true';
    });

    const setUseAinovisTemplates = (use: boolean) => {
        setUseAinovisTemplatesState(use);
        localStorage.setItem(USE_AINOVIS_TEMPLATES_KEY, String(use));
    };

    return (
        <SettingsContext.Provider value={{ 
            language,
            setLanguage,
            useAinovisTemplates,
            setUseAinovisTemplates,
        }}>
            {children}
        </SettingsContext.Provider>
    );
}

export function useSettings() {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
}
